import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Table, Tag, Button, Select, Typography, Space, Statistic, message, Checkbox, Tooltip, Empty, Input, Badge, Drawer, Alert } from 'antd';
import { SwapOutlined, SearchOutlined, CheckCircleOutlined, FilterOutlined, DollarOutlined, ThunderboltOutlined, LineChartOutlined, CloseOutlined, AppstoreOutlined, ExperimentOutlined, BarChartOutlined } from '@ant-design/icons';
import { modelAPI } from '../services/api';
import { t } from '../utils/i18n';

const { Title, Text } = Typography;

const PriceCompare = () => {
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [compareDrawerVisible, setCompareDrawerVisible] = useState(false);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await modelAPI.getSquareModels({});
      if (res.code === 0) {
        const modelList = res.data?.list || res.data?.items || res.data || [];
        setModels(modelList);
      }
    } catch (error) {
      console.error('获取模型列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (modelName) => {
    if (selectedModels.includes(modelName)) {
      const newSelected = selectedModels.filter(m => m !== modelName);
      setSelectedModels(newSelected);
      if (newSelected.length < 2) {
        setCompareDrawerVisible(false);
      }
    } else if (selectedModels.length < 4) {
      const newSelected = [...selectedModels, modelName];
      setSelectedModels(newSelected);
      if (newSelected.length >= 2) {
        setCompareDrawerVisible(true);
      }
    } else {
      message.warning(t('max_models_compare_warning'));
    }
  };

  // 提取供应商列表
  const providers = useMemo(() => {
    const set = new Set();
    models.forEach(m => {
      if (m.provider?.display_name) set.add(m.provider.display_name);
    });
    return [...set];
  }, [models]);

  // 模型类型
  const modelTypes = useMemo(() => {
    const set = new Set();
    models.forEach(m => {
      if (m.type) set.add(m.type);
    });
    return [...set];
  }, [models]);

  // 过滤后的模型
  const filteredModels = useMemo(() => {
    return models.filter(m => {
      if (selectedProvider && m.provider?.display_name !== selectedProvider) return false;
      if (selectedType && m.type !== selectedType) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          m.model_name?.toLowerCase().includes(search) ||
          m.display_name?.toLowerCase().includes(search) ||
          m.provider?.display_name?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [models, selectedProvider, selectedType, searchText]);

  // 统计信息
  const stats = useMemo(() => {
    const prices = filteredModels.filter(m => m.input_price).map(m => parseFloat(m.input_price));
    return {
      total: filteredModels.length,
      withPrice: filteredModels.filter(m => m.input_price).length,
      cheapest: prices.length > 0 ? Math.min(...prices) : 0,
      providers: providers.length,
    };
  }, [filteredModels, providers]);

  // 价格比较列 - 勾选框放在第一列
  const columns = [
    {
      title: <div style={{ textAlign: 'center' }}>{t('select') || '选择'}</div>,
      key: 'select',
      width: 60,
      fixed: 'left',
      render: (_, record) => (
        <div style={{ 
          backgroundColor: '#f0f5ff', 
          borderRadius: 4, 
          padding: '4px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Checkbox
            checked={selectedModels.includes(record.model_name)}
            onChange={() => handleModelSelect(record.model_name)}
            disabled={!selectedModels.includes(record.model_name) && selectedModels.length >= 4}
            style={{ margin: 0 }}
          />
        </div>
      ),
    },
    {
      title: t('model'),
      dataIndex: 'display_name',
      key: 'display_name',
      width: 180,
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{text || record.model_name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{record.model_name}</Text>
        </div>
      ),
    },
    {
      title: t('provider'),
      dataIndex: ['provider', 'display_name'],
      width: 100,
      render: (text, record) => (
        <Tag color={record.provider?.color || 'blue'}>{text}</Tag>
      ),
    },
    {
      title: t('type'),
      dataIndex: 'type',
      width: 90,
      render: (type) => {
        const colors = { 
          text: 'blue', 
          image: 'orange', 
          embeddings: 'green', 
          audio: 'purple', 
          video: 'red',
          chat: 'cyan'
        };
        return <Tag color={colors[type] || 'default'}>{type || '-'}</Tag>;
      },
    },
    {
      title: t('context_length'),
      dataIndex: 'context_length',
      width: 100,
      render: (len) => {
        if (!len) return '-';
        // 处理字符串格式如 "32K", "128K", "1M" 或纯数字 "128000"
        const str = String(len).toUpperCase();
        if (str.includes('M')) {
          const num = parseFloat(str);
          return isNaN(num) ? '-' : `${num}M`;
        }
        if (str.includes('K')) {
          const num = parseFloat(str);
          return isNaN(num) ? '-' : `${num}K`;
        }
        // 纯数字，转换为K
        const num = parseInt(str);
        return isNaN(num) ? '-' : `${(num / 1000).toFixed(0)}K`;
      },
      sorter: (a, b) => {
        const getNum = (val) => {
          if (!val) return 0;
          const str = String(val).toUpperCase();
          if (str.includes('M')) return parseFloat(str) * 1000;
          if (str.includes('K')) return parseFloat(str);
          return parseInt(str) / 1000;
        };
        return getNum(a.context_length) - getNum(b.context_length);
      },
    },
    {
      title: t('input_price'),
      dataIndex: 'input_price',
      width: 120,
      render: (price) => price ? (
        <Text strong style={{ color: '#52c41a' }}>
          ${parseFloat(price).toFixed(6)}
        </Text>
      ) : '-',
      sorter: (a, b) => (a.input_price || 0) - (b.input_price || 0),
    },
    {
      title: t('output_price'),
      dataIndex: 'output_price',
      width: 120,
      render: (price) => price ? (
        <Text style={{ color: '#fa8c16' }}>
          ${parseFloat(price).toFixed(6)}
        </Text>
      ) : '-',
      sorter: (a, b) => (a.output_price || 0) - (b.output_price || 0),
    },
  ];

  // 生成对比数据
  const compareData = useMemo(() => {
    if (selectedModels.length < 2) return [];

    const selectedModelData = selectedModels.map(name => 
      models.find(m => m.model_name === name)
    ).filter(Boolean);

    const inputPrices = selectedModelData.filter(x => x.input_price).map(x => parseFloat(x.input_price));
    const outputPrices = selectedModelData.filter(x => x.output_price).map(x => parseFloat(x.output_price));
    const minInput = inputPrices.length > 0 ? Math.min(...inputPrices) : null;
    const minOutput = outputPrices.length > 0 ? Math.min(...outputPrices) : null;

    return [
      {
        key: 'input',
        label: t('input_price'),
        icon: <DollarOutlined />,
        values: selectedModelData.map(m => ({
          name: m.display_name || m.model_name,
          value: m.input_price ? `$${parseFloat(m.input_price).toFixed(6)}` : '-',
          isMin: m.input_price && parseFloat(m.input_price) === minInput,
        })),
      },
      {
        key: 'output',
        label: t('output_price'),
        icon: <DollarOutlined />,
        values: selectedModelData.map(m => ({
          name: m.display_name || m.model_name,
          value: m.output_price ? `$${parseFloat(m.output_price).toFixed(6)}` : '-',
          isMin: m.output_price && parseFloat(m.output_price) === minOutput,
        })),
      },
      {
        key: 'context',
        label: t('context_length'),
        icon: <LineChartOutlined />,
        values: selectedModelData.map(m => {
          const len = m.context_length;
          let display = '-';
          if (len) {
            const str = String(len).toUpperCase();
            if (str.includes('M')) {
              display = parseFloat(str) > 0 ? `${str}` : '-';
            } else if (str.includes('K')) {
              display = parseFloat(str) > 0 ? `${str}` : '-';
            } else {
              const num = parseInt(str);
              display = !isNaN(num) ? `${(num / 1000).toFixed(0)}K` : '-';
            }
          }
          return {
            name: m.display_name || m.model_name,
            value: display,
            isMin: false,
          };
        }),
      },
      {
        key: 'provider',
        label: t('provider'),
        icon: <FilterOutlined />,
        values: selectedModelData.map(m => ({
          name: m.display_name || m.model_name,
          value: m.provider?.display_name || '-',
          isMin: false,
        })),
      },
    ];
  }, [selectedModels, models, t]);

  const handleCloseDrawer = () => {
    setCompareDrawerVisible(false);
  };

  const handleClearAll = () => {
    setSelectedModels([]);
    setCompareDrawerVisible(false);
  };

  return (
    <div style={{ padding: 0, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* 头部 */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '32px 24px 48px', color: '#fff' }}>
        <Row gutter={24} align="middle">
          <Col flex="none">
            <div style={{ width: 48, height: 48, background: 'rgba(255,255,255,0.2)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SwapOutlined style={{ fontSize: 24 }} />
            </div>
          </Col>
          <Col flex="auto">
            <Title level={3} style={{ color: '#fff', margin: 0 }}>{t('price_compare') || '价格对比'}</Title>
            <Text style={{ color: 'rgba(255,255,255,0.8)', marginTop: 8, display: 'block' }}>
              {t('price_compare_desc') || '比较不同模型的价格，选择最具性价比的方案'}
            </Text>
          </Col>
          {selectedModels.length >= 2 && (
            <Col flex="none">
              <Button 
                type="primary" 
                ghost 
                onClick={() => setCompareDrawerVisible(true)}
                style={{ borderColor: '#fff', color: '#fff' }}
              >
                {t('model_comparison')} ({selectedModels.length})
              </Button>
            </Col>
          )}
        </Row>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginTop: -24, marginBottom: 24, padding: '0 24px' }}>
        <Col span={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic 
              title={<Text type="secondary">{t('total_models') || '模型总数'}</Text>} 
              value={stats.total} 
              prefix={<ThunderboltOutlined style={{ color: '#667eea' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic 
              title={<Text type="secondary">{t('with_price') || '有价格'}</Text>} 
              value={stats.withPrice} 
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic 
              title={<Text type="secondary">{t('cheapest_input') || '最低输入价'}</Text>} 
              value={stats.cheapest ? `$${stats.cheapest.toFixed(6)}` : '-'} 
              valueStyle={{ color: '#fa8c16', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic 
              title={<Text type="secondary">{t('providers_count') || '供应商'}</Text>} 
              value={stats.providers} 
              prefix={<FilterOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选区域 */}
      <Card style={{ margin: '0 24px 24px', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle" wrap>
              <Input
                placeholder={t('search_model') || '搜索模型...'}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 200 }}
                allowClear
              />
              <Select
                placeholder={t('filter_by_provider') || '筛选供应商'}
                value={selectedProvider}
                onChange={setSelectedProvider}
                allowClear
                style={{ width: 150 }}
                options={providers.map(p => ({ value: p, label: p }))}
              />
              <Select
                placeholder={t('filter_by_type') || '筛选类型'}
                value={selectedType}
                onChange={setSelectedType}
                allowClear
                style={{ width: 120 }}
                options={modelTypes.map(t => ({ value: t, label: t }))}
              />
            </Space>
          </Col>
          <Col flex="none">
            <Space>
              <Text type="secondary">{t('selected')}:</Text>
              <Badge count={selectedModels.length} style={{ backgroundColor: '#667eea' }} showZero color="#667eea" />
              <Text type="secondary">/ 4</Text>
              {selectedModels.length > 0 && (
                <Button type="link" size="small" onClick={handleClearAll} danger>
                  {t('clear_all') || '清除'}
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 模型列表 - 只有在没有选择2个以上模型时才显示满宽度 */}
      <div style={{ padding: '0 24px 24px' }}>
        <Card 
          title={
            <Space>
              <span>{t('model_list') || '模型列表'}</span>
              <Tag>{filteredModels.length}</Tag>
            </Space>
          }
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('max_compare_tip') || '最多选择4个模型进行对比'}
            </Text>
          }
          style={{ borderRadius: 12 }}
        >
          <Table
            columns={columns}
            dataSource={filteredModels}
            rowKey="model_name"
            loading={loading}
            size="middle"
            scroll={{ x: 800 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t('total')}: ${total}`,
              locale: {
                items_per_page: t('per_page') || '/ 页',
                prev_page: t('prev_page') || '上一页',
                next_page: t('next_page') || '下一页',
                jump_to: t('jump_to') || '跳至',
                page: t('page') || '页',
              },
            }}
            locale={{ 
              emptyText: loading ? t('loading') : t('no_models_found')
            }}
          />
        </Card>
      </div>

      {/* 对比抽屉 - 悬浮在右侧 */}
      <Drawer
        title={
          <Space>
            <SwapOutlined style={{ color: '#667eea' }} />
            <span>{t('model_comparison')}</span>
            <Badge count={selectedModels.length} style={{ backgroundColor: '#667eea' }} />
          </Space>
        }
        placement="right"
        width={520}
        open={compareDrawerVisible}
        onClose={handleCloseDrawer}
        closable={true}
        extra={
          <Button type="link" size="small" onClick={handleClearAll} danger>
            {t('clear_all')}
          </Button>
        }
      >
        {/* 说明区域 */}
        <Alert
          message={t('compare_result_explain')}
          description={
            <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: 12, color: '#666' }}>
              <li><Text strong style={{ color: '#52c41a' }}>{t('compare_green_label')}</Text> = {t('compare_green_tip')}</li>
              <li>{t('compare_price_context')}</li>
              <li>{t('compare_overall_hint')}</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        {compareData.map(row => (
          <div key={row.key} style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 12 }}>
              <span style={{ color: '#667eea' }}>{row.icon}</span>
              <Text strong>{row.label}</Text>
              {row.key !== 'context' && row.key !== 'provider' && (
                <Tooltip title={t('compare_green_tip')}>
                  <Tag color="green" style={{ fontSize: 10 }}>{t('lowest_price')}</Tag>
                </Tooltip>
              )}
            </Space>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${row.values.length}, 1fr)`, gap: 12 }}>
              {row.values.map((item, idx) => (
                <Card 
                  key={idx}
                  size="small" 
                  style={{ 
                    textAlign: 'center',
                    background: item.isMin ? '#f6ffed' : '#fafafa',
                    borderColor: item.isMin ? '#52c41a' : '#e8e8e8',
                    borderWidth: item.isMin ? 2 : 1,
                  }}
                  styles={{ body: { padding: '16px 12px' } }}
                >
                  <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 8 }}>
                    {item.name}
                  </Text>
                  <Text 
                    strong 
                    style={{ 
                      fontSize: 16,
                      color: item.isMin ? '#52c41a' : '#333',
                      display: 'block',
                    }}
                  >
                    {item.value}
                  </Text>
                  {item.isMin && (
                    <Tag color="success" style={{ marginTop: 8, fontSize: 11 }}>
                      <CheckCircleOutlined /> {t('lowest_price')}
                    </Tag>
                  )}
                </Card>
              ))}
            </div>
          </div>
        ))}

        {/* 推荐区域 */}
        <Card style={{ marginTop: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }} styles={{ body: { padding: 16 } }}>
          <Text strong style={{ color: '#fff', fontSize: 14, display: 'block', marginBottom: 8 }}>
            💡 {t('compare_recommendation')}
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
            {selectedModels.length >= 2 ? (
              (() => {
                const bestModel = compareData.filter(r => r.key === 'input').flatMap(r => 
                  r.values.filter(v => v.isMin).map(v => v.name)
                )[0];
                return bestModel ? (
                  <span>{t('compare_best_input', { model: bestModel })}</span>
                ) : (
                  <span>{t('compare_no_best')}</span>
                );
              })()
            ) : (
              <span>{t('compare_select_2_hint')}</span>
            )}
          </Text>
        </Card>
      </Drawer>

      {/* 右侧悬浮按钮 */}
      <div style={{
        position: 'fixed',
        right: 24,
        bottom: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        zIndex: 100,
      }}>
        <Tooltip title={t('all_models', '全部模型')} placement="left">
          <Button
            shape="circle"
            size="large"
            icon={<AppstoreOutlined />}
            onClick={() => window.location.href = '/dashboard/models'}
            style={{
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
            }}
          />
        </Tooltip>
        <Tooltip title={t('my_training', '我的训练')} placement="left">
          <Button
            shape="circle"
            size="large"
            icon={<ExperimentOutlined />}
            onClick={() => window.location.href = '/dashboard/models?tab=my-training'}
            style={{
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
            }}
          />
        </Tooltip>
        <Tooltip title={t('price_compare', '价格对比')} placement="left">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<BarChartOutlined />}
            style={{
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default PriceCompare;
